import {
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography
} from "@mui/material";
import { User } from "interfaces/user";
import { UserAvatar } from "components/Avatar/UserAvatar";
import { DateWithTooltip } from "components/DateWithTooltip";
import React from "react";
import buildUserName from "core/utils/build_user_name";
import { styled } from "@mui/material/styles";
import {
  StreamCardMenu,
  StreamCardMenuOption
} from "components/CaseManagement/CaseStream/StreamCardMenu";

export interface StreamCardProps {
  user: User;
  datetime: string;
  label: string;
  headerAdornment?: React.ReactNode;
  menuOptions?: StreamCardMenuOption[];
}

export const StreamCard = ({
  user,
  datetime,
  label,
  headerAdornment,
  menuOptions,
  children
}: React.PropsWithChildren<StreamCardProps>): JSX.Element => {
  const username = buildUserName(user);

  return (
    <MuiCard>
      <CardContent>
        <CardHeader>
          <TitleWrapper>
            <UserAvatar name={username} />
            <div>
              <HeaderUsernameText variant="body1">
                {username}
              </HeaderUsernameText>
              <HeaderDateText>
                <DateWithTooltip date={datetime} showTime />
              </HeaderDateText>
            </div>
          </TitleWrapper>
          <HeaderRightSection>
            <CenterAlignedRow>
              {headerAdornment}
              <Label variant="caption">{label}</Label>
              {menuOptions && <StreamCardMenu menuOptions={menuOptions} />}
            </CenterAlignedRow>
          </HeaderRightSection>
        </CardHeader>
        {children}
      </CardContent>
    </MuiCard>
  );
};

const CardHeader = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
`;

const TitleWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const HeaderRightSection = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const CenterAlignedRow = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Label = styled(Typography)`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
`;

const CardContent = styled(MuiCardContent)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderUsernameText = styled(Typography)`
  font-size: 13px;
`;

const HeaderDateText = styled("div")`
  font-size: 12px;
  color: ${props => props.theme.palette.grey["700"]};
`;
